import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isEmpty } from 'lodash';
import { RESULT_STATUS, getExecResultCodeStatus } from '@/pages/Maintenance/Executive/Record';
import { getRecordBasic, getRecordDetail } from '@/services/execRecordService';

const initialState = {
  // 执行详情
  summary: {}, // 总览数据
  detailLogVO: {}, // 详情的总览数据，详情自轮询时使用
  steps: [], // 执行步骤
  currentStep: null, // 当前执行步骤详细内容
  currentSubStep: null, // 二级
  servers: [],
  variables: [],
  currentHost: null,
  logs: [],
  execs: [],
  canRefresh: null,

  recordHostFetching: false,
  recordDetailFetching: false,
};

export const fetchRecordHost = createAsyncThunk('execRecord/fetchRecordHost', async (params) => {
  const response = await getRecordDetail(params);
  return response.data;
});

export const fetchRecordBasic = createAsyncThunk('execRecord/fetchRecordBasic', async (params) => {
  const response = await getRecordBasic(params);
  return response.data;
});

export const refreshSteps = createAsyncThunk('execRecord/refreshSteps', async (params) => {
  const response = await getRecordBasic(params);
  return response.data;
});

export const fetchRecordDetail = createAsyncThunk(
  'execRecord/fetchRecordDetail',
  async (params) => {
    const response = await getRecordDetail(params);
    return response.data;
  },
);

export const execRecordSlice = createSlice({
  name: 'execRecord',
  initialState,
  reducers: {
    resetRecordDetail: () => {
      return initialState; // 完全初始化
    },
    // 设置是否允许自动刷新
    setCanRefresh: (state, action) => {
      state.canRefresh = action.payload;
    },
    // 设置当前执行步骤（一级）
    setRecordStep: (state, action) => {
      state.currentStep = action.payload;
      state.currentHost = [];
      state.variables = []; // 初始化
      state.logs = []; // 初始化
      state.execs = []; // 初始化
      state.canRefresh = null;
    },
    // 设置当前执行步骤（二级）不需要重置主机
    setRecordSubStep: (state, action) => {
      state.currentSubStep = action.payload;
      state.variables = []; // 初始化
      state.logs = []; // 初始化
      state.execs = []; // 初始化
      state.canRefresh = null;
    },
    // 设置当前主机或K8s节点
    setCurrentHost: (state, action) => {
      state.currentHost = action.payload;
      state.variables = []; // 初始化
      state.logs = []; // 初始化
      state.execs = []; // 初始化
      state.canRefresh = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecordHost.pending, (state) => {
        // state.servers = [];
        state.recordHostFetching = true;
      })
      .addCase(fetchRecordHost.fulfilled, (state, action) => {
        const { hostsSimpleVOList: hosts = [] } = action.payload;
        state.servers = hosts;
        let unFinishedHost;
        for (let index = 0; index < hosts.length; index++) {
          const host = hosts[index];
          if (
            getExecResultCodeStatus(host.execResultCode) === RESULT_STATUS.PROCESSING ||
            getExecResultCodeStatus(host.execResultCode) === RESULT_STATUS.ERROR
          ) {
            unFinishedHost = host;
            break;
          }
        }
        state.recordHostFetching = false;
        state.currentHost = unFinishedHost;
        if (
          !unFinishedHost &&
          hosts[0] &&
          getExecResultCodeStatus(hosts[0].execResultCode) !== RESULT_STATUS.READY
        ) {
          state.currentHost = hosts[0];
        }
      })
      .addCase(fetchRecordBasic.fulfilled, (state, action) => {
        const { logVO, opsExecLogStepVO } = action.payload;
        state.summary = logVO;
        const steps = opsExecLogStepVO.map((item) => {
          if (item.opsExecLogStepVO) {
            item.subSteps = item.opsExecLogStepVO.map((sub) => ({
              ...sub,
              parentId: item.stepId,
              keyId: `${item.stepId}-sub-${sub.stepId}`,
            }));
          }
          return item;
        });
        state.steps = steps;
        // 寻找展示的步骤
        // 部分执行寻找execResultCode最后一个非空节点，否则放置最后一个节点
        let found, foundSub;

        outer: for (let index = 0; index < steps.length; index++) {
          const step = steps[index];
          foundSub = null;
          // 遇空结束
          if (getExecResultCodeStatus(step.execResultCode) === RESULT_STATUS.READY) {
            break;
          }
          found = step;
          if (!isEmpty(step.subSteps)) {
            for (let subIndex = 0; subIndex < step.subSteps.length; subIndex++) {
              const subStep = step.subSteps[subIndex];
              if (getExecResultCodeStatus(subStep.execResultCode) === RESULT_STATUS.READY) {
                break outer; // 全部结束
              }
              foundSub = subStep;
            }
          }
        }
        // 可能没有subStep
        if (!state.currentStep) {
          state.currentStep = found;

          if (!state.currentSubStep) {
            state.currentSubStep = foundSub;
          }
        }
      })
      .addCase(fetchRecordDetail.pending, (state) => {
        state.recordDetailFetching = true;
      })
      .addCase(fetchRecordDetail.fulfilled, (state, action) => {
        state.recordDetailFetching = false;
        const {
          runDetailLogVO,
          componentVariableList,
          hostsSimpleVOList: hosts = [],
          logVO,
        } = action.payload;
        state.servers = hosts; // 刷新host状态
        state.logs = runDetailLogVO;
        state.detailLogVO = logVO; // 详情的状态，详情轮询用
        state.variables = componentVariableList;
      })
      .addCase(fetchRecordDetail.rejected, (state) => {
        state.recordDetailFetching = false;
      })
      .addCase(refreshSteps.fulfilled, (state, action) => {
        const { opsExecLogStepVO, logVO } = action.payload;
        // 刷新左侧步骤
        const steps = opsExecLogStepVO.map((item) => {
          if (item.opsExecLogStepVO) {
            item.subSteps = item.opsExecLogStepVO.map((sub) => ({
              ...sub,
              parentId: item.stepId,
              keyId: `${item.stepId}-sub-${sub.stepId}`, // 保持key一致
            }));
          }
          return item;
        });
        state.steps = steps;
        state.summary = logVO;
      });
  },
});

export const { setRecordStep, setRecordSubStep, setCurrentHost, resetRecordDetail, setCanRefresh } =
  execRecordSlice.actions;
export default execRecordSlice.reducer;
